import type { CSSProperties } from 'react';
import { storefrontAssetUrl } from '../../lib/api';

/**
 * Background style for a storefront banner/slide: an uploaded image (by R2 key)
 * takes precedence; otherwise the configured CSS gradient; otherwise a neutral
 * fallback. Image is cover-centered.
 *
 * `dim` (default true) lays a dark wash over the image so white overlay text
 * stays legible. Pass `false` for a FULL-IMAGE banner — one whose caption fields
 * are all empty, so the artwork carries its own baked-in copy. Dimming those
 * just muddies the client's artwork for no reason.
 */
export function bannerStyle(opts: {
  imageKey?: string;
  gradient?: string;
  fallback?: string;
  dim?: boolean;
}): CSSProperties {
  // Brand gradient follows the tenant (`--brand-gradient`; baseline byte-identical).
  const fallback = opts.fallback ?? 'var(--brand-gradient)';
  if (opts.imageKey) {
    const url = `url("${storefrontAssetUrl(opts.imageKey)}")`;
    const wash = 'linear-gradient(rgba(0,0,0,.28),rgba(0,0,0,.28))';
    return {
      backgroundImage: opts.dim === false ? url : `${wash}, ${url}`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    };
  }
  return { background: opts.gradient || fallback };
}

/** True when any caption field carries real text — drives `dim` above. */
export function hasBannerText(...values: Array<string | undefined | null>): boolean {
  return values.some((v) => !!v && v.trim() !== '');
}
